<template>
  <div
    class="action-panel product-counter-offers-panel"
    data-panel="offer"
    style="--panel-margin-left: 0"
  >
    <button data-close-panel>Close ✕</button>

    <div class="action-panel__content">
      <div class="coffers">
        <h3>Counter offers to your product:</h3>

        <ul class="coffers__list">
          <li
            v-for="(offer, idx) in localOffers"
            :key="offer.id || idx"
            data-panel-group
          >
            <div class="coffers__item" :class="{ approved: isApprovedOffer(offer) }">
              <div class="form-block__body">
                <div class="form-row-grid">
                  <label>Price:</label>
                  <span>
                    <strong>{{ formatPrice(offer) }}</strong>
                    <MarketplaceUnreadBadge :count="offerBulb(offer)" />
                  </span>
                </div>

                <div class="form-row-grid">
                  <label>Terms:</label>
                  <div class="text">
                    <p
                      v-for="(p, pIdx) in termsParagraphs(offer)"
                      :key="pIdx"
                    >
                      {{ p }}
                    </p>
                  </div>
                </div>

                <div class="btns-group--right">
                  <span v-if="isApprovedOffer(offer)" class="approved-text">
                    Congratulations! This offer is approved by {{ buyerName(offer) }}
                  </span>
                  <a
                    class="btn btn-primary"
                    href="#"
                    data-toggle-panel="messages"
                    :data-panel-unread="offerMessageBulb(offer)"
                    @click.prevent
                  >
                    Messages
                    <MarketplaceUnreadBadge :count="offerMessageBulb(offer)" />
                  </a>

                  <a
                    v-if="canApproveOffer(offer)"
                    class="btn btn-approve"
                    href="#"
                    @click.prevent="approveOffer(offer)"
                  >
                    Approve
                  </a>
                </div>
              </div>
            </div>

            <div
              class="action-panel offer-messages-panel"
              data-panel="messages"
              :data-offer-id="offer.id"
            >
              <button data-close-panel>Close ✕</button>

              <div class="action-panel__content">
                <MessageThread
                  :ref="`offerThread-${offer.id}`"
                  :messages="offer.messages"
                  :isSending="sendingOfferId === offer.id"
                  :meAvatar="meAvatar"
                  :readOnly="isApprovedOffer(offer)"
                  @send="handleSendOffer(offer, $event)"
                  @like="(msg) => handleLikeMessage(offer, msg)"
                  @unlike="(msg) => handleUnlikeMessage(offer, msg)"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import MatchmakingService from '@/services/MatchmakingService';
import MessageThread from '@/components/matchmaking/MessageThread.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';
import marketplaceOfferDetails from '@/mixins/marketplaceOfferDetails';

export default {
  name: 'ProductCounterOffersPanel',

  components: {
    MessageThread,
    MarketplaceUnreadBadge,
  },

  mixins: [marketplaceOfferDetails],

  props: {
    productId: {
      type: [Number, String],
      required: true,
    },
    marketplaceId: {
      type: [Number, String],
      default: null,
    },
    role: {
      type: String,
      default: 'seller',
    },
    offers: {
      type: Array,
      default: () => [],
    },
    canApproveOffers: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      localOffers: [],
      sendingOfferId: null,
      isPrefetching: false,
      isMarkingNewOffers: false,
      pendingMessagesOpenDetails: {},
      syncingOfferThreads: {},
      // eslint-disable-next-line vue/no-reserved-keys
      _visibilityObserver: null,
    };
  },

  computed: {
    offerListKey() {
      return 'localOffers';
    },

    me() {
      return this.$store.getters.getMe || {};
    },

    meAvatar() {
      return this.me?.avatar || '';
    },

  },

  watch: {
    offers: {
      immediate: true,
      handler(val) {
        this.localOffers = this.cloneOffers(val);

        this.$nextTick(() => {
          if (this.isPanelVisible()) this.prefetchOffersDetails();
        });
      },
    },
  },

  methods: {
    isPanelVisible() {
      return Boolean(this.$el && this.$el.classList && this.$el.classList.contains('is-visible'));
    },

    offerBulb(offer) {
      return this.$store.getters.productOfferBulb(this.role, offer && offer.id);
    },

    offerMessageBulb(offer) {
      return this.$store.getters.productOfferThreadBulb(this.role, offer && offer.id);
    },

    onPanelShown(e) {
      const detail = e && e.detail;
      if (!detail || !this.$el) return;
      if (detail.panel !== 'offer' || detail.target !== this.$el) return;

      if (this.role === 'seller') this.markNewOffersRead();
    },

    async markNewOffersRead() {
      if (this.isMarkingNewOffers) return;
      const offersMap = this.$store.getters.marketplaceBlock('seller').offers || {};
      const ids = (Array.isArray(this.localOffers) ? this.localOffers : [])
        .map((offer) => Number(offer && offer.id))
        .filter((id) => Number.isInteger(id) && id > 0)
        .filter((id) => (Number(offersMap[id]) || 0) > 0);
      if (!this.productId || !ids.length) return;

      this.isMarkingNewOffers = true;
      try {
        await MatchmakingService.markMarketplaceProductOffersRead(this.productId, ids);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.isMarkingNewOffers = false;
      }
    },

    onOfferMessagesOpenRequest(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'messages') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      const offerId = detail.target.getAttribute && detail.target.getAttribute('data-offer-id');
      if (!offerId) return;
      this.$set(this.pendingMessagesOpenDetails, String(offerId), detail);
      this.syncOfferThread(offerId);
    },

    onOfferMessagesRefresh(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'messages') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      const offerId = detail.target.getAttribute && detail.target.getAttribute('data-offer-id');
      if (!offerId) return;
      this.syncOfferThread(offerId);
    },

    isOfferThreadVisible(offerId) {
      if (!this.$el || !this.$el.isConnected) return false;
      if (!this.isPanelVisible()) return false;
      const panel = this.$el.querySelector(`.offer-messages-panel[data-offer-id="${offerId}"]`);
      return Boolean(panel && panel.classList.contains('is-visible'));
    },

    async syncOfferThread(offerId) {
      const key = String(offerId);
      if (this.syncingOfferThreads[key]) return;
      this.$set(this.syncingOfferThreads, key, true);
      try {
        const offer = this.localOffers.find((o) => String(o && o.id) === key);
        if (!offer) return;
        const hadUnread = this.offerMessageBulb(offer) > 0;

        const ok = await this.loadOfferDetails(offer, true);
        const openDetail = this.pendingMessagesOpenDetails[key];
        this.$delete(this.pendingMessagesOpenDetails, key);
        if (!ok) return;

        if (openDetail) {
          const opened = typeof openDetail.open === 'function' && openDetail.open();
          if (!opened) return;
        }

        await this.$nextTick();
        if (!this.isOfferThreadVisible(key)) return;

        const fresh = this.localOffers.find((o) => String(o && o.id) === key) || offer;
        if (hadUnread) await this.scrollToOfferThread(fresh.id);
        await this.markOfferMessagesRead(fresh);
      } finally {
        this.$delete(this.pendingMessagesOpenDetails, key);
        this.$delete(this.syncingOfferThreads, key);
      }
    },

    cloneOffers(val) {
      const src = Array.isArray(val) ? val : [];
      const cloned = JSON.parse(JSON.stringify(src));

      return cloned.map((o) => ({
        ...o,
        messages: Array.isArray(o?.messages) ? o.messages : [],
        __detailsLoaded: Boolean(Array.isArray(o?.messages) && o.messages.length),
        __detailsLoading: false,
        __detailsError: '',
      }));
    },

    setupVisibilityObserver() {
      // eslint-disable-next-line no-underscore-dangle
      if (this._visibilityObserver || !this.$el) return;
      // eslint-disable-next-line no-undef,no-underscore-dangle
      this._visibilityObserver = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.type !== 'attributes' || m.attributeName !== 'class') return;
          if (this.isPanelVisible()) {
            this.prefetchOffersDetails();
          }
        });
      });

      // eslint-disable-next-line no-underscore-dangle
      this._visibilityObserver.observe(this.$el, {
        attributes: true,
        attributeFilter: ['class'],
      });

      if (this.isPanelVisible()) {
        this.prefetchOffersDetails();
      }
    },

    teardownVisibilityObserver() {
      // eslint-disable-next-line no-underscore-dangle
      if (this._visibilityObserver) {
        // eslint-disable-next-line no-underscore-dangle
        this._visibilityObserver.disconnect();
        // eslint-disable-next-line no-underscore-dangle
        this._visibilityObserver = null;
      }
    },

    async prefetchOffersDetails() {
      if (this.isPrefetching) return;
      if (!Array.isArray(this.localOffers) || !this.localOffers.length) return;

      this.isPrefetching = true;
      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const offer of this.localOffers) {
          // eslint-disable-next-line no-await-in-loop
          await this.loadOfferDetails(offer);
        }
      } finally {
        this.isPrefetching = false;
      }
    },

    formatPrice(offer) {
      const value = offer?.price ?? offer?.amount ?? offer?.value;
      const currency = offer?.currency || 'euro';
      if (value === null || value === undefined || value === '') return '—';
      return `${value} ${currency}`;
    },

    termsParagraphs(offer) {
      const raw = offer?.terms
        ?? offer?.term
        ?? offer?.detail
        ?? offer?.description
        ?? offer?.text
        ?? '';

      const str = String(raw || '').trim();
      if (!str) return ['—'];

      // Split by empty lines or single newlines.
      return str
        .split(/\n\s*\n|\r\n\s*\r\n|\r\s*\r|\n|\r/g)
        .map((p) => p.trim())
        .filter(Boolean);
    },

    approveOffer(offer) {
      this.$emit('approve', { productId: this.productId, offer });
    },

    buyerName(offer) {
      const user = offer?.user;
      if (user && (user.name || user.profile?.name)) {
        return user.name || user.profile?.name;
      }
      return "Buyer's Name";
    },

    isApprovedOffer(offer) {
      const statusName = String(offer?.status_name || offer?.statusName || '').toLowerCase();
      if (statusName.includes('approved')) return true;

      const statusVal = offer?.status;
      if (Array.isArray(statusVal)) {
        const active = statusVal.find((s) => s && s.active);
        if (active) {
          const name = String(active.name || '').toLowerCase();
          if (name.includes('approved')) return true;
          if (Number(active.id) === 3 || Number(active.id) === 2) return true;
        }
      }
      if (statusVal && typeof statusVal === 'object' && !Array.isArray(statusVal)) {
        const name = String(statusVal.name || '').toLowerCase();
        if (name.includes('approved')) return true;
        if (Number(statusVal.id) === 3 || Number(statusVal.id) === 2) return true;
      }

      const statusNum = Number(offer?.status);
      return statusNum === 3 || statusNum === 2;
    },

    canApproveOffer(offer) {
      if (!this.canApproveOffers) return false;
      return !this.isApprovedOffer(offer);
    },

    async handleLikeMessage(offer, msg) {
      if (!msg?.id || !offer?.messages) return;
      try {
        await MatchmakingService.addLikeToMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = offer.messages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', true);
          this.$set(m, 'is_liked', true);
          const cnt = Number(m?.likes_count ?? m?.likes ?? 0) + 1;
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Like failed:', e);
      }
    },

    async handleUnlikeMessage(offer, msg) {
      if (!msg?.id || !offer?.messages) return;
      try {
        await MatchmakingService.removeLikeFromMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = offer.messages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', false);
          this.$set(m, 'is_liked', false);
          const cnt = Math.max(0, Number(m?.likes_count ?? m?.likes ?? 1) - 1);
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Unlike failed:', e);
      }
    },

    async handleSendOffer(offer, text) {
      if (!offer || !text) return;

      const offerId = offer.id;
      this.sendingOfferId = offerId;

      try {
        const payload = {
          offer_id: offerId,
          text,
        };

        // Swagger: POST /marketplace-product-offers/message
        const { data } = await MatchmakingService.marketplaceProductOfferMessage(payload);

        if (!Array.isArray(offer.messages)) this.$set(offer, 'messages', []);
        offer.messages.push(data);
      } catch (e) {
        // Fallback: optimistic message so UI still works even if endpoint differs.
        const optimistic = {
          id: `tmp-${Date.now()}`,
          text,
          created_at: new Date().toISOString(),
          user: {
            name: this.me?.name || 'Me',
            avatar: this.me?.avatar || '',
            role: this.me?.role || 'seller',
          },
          __optimistic: true,
        };

        if (!Array.isArray(offer.messages)) this.$set(offer, 'messages', []);
        offer.messages.push(optimistic);
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.sendingOfferId = null;
      }
    },
  },

  mounted() {
    this.setupVisibilityObserver();
    window.addEventListener('phase2-action-panel-shown', this.onPanelShown);
    window.addEventListener('phase2-action-panel-open-request', this.onOfferMessagesOpenRequest);
    window.addEventListener('phase2-action-panel-refresh', this.onOfferMessagesRefresh);
  },

  beforeDestroy() {
    this.teardownVisibilityObserver();
    window.removeEventListener('phase2-action-panel-shown', this.onPanelShown);
    window.removeEventListener('phase2-action-panel-open-request', this.onOfferMessagesOpenRequest);
    window.removeEventListener('phase2-action-panel-refresh', this.onOfferMessagesRefresh);
  },
};
</script>

<style scoped>
.product-counter-offers-panel {
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.product-counter-offers-panel > .action-panel__content,
.coffers,
.coffers__list,
.coffers__list > li,
.coffers__item {
  width: 100%;
  box-sizing: border-box;
}

.coffers {
  margin-left: 0;
}

.offer-messages-panel {
  --panel-margin-left: 0;
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  box-sizing: border-box;
}

/* Minor helper so "—" doesn't look like a paragraph block */
.text p {
  margin: 0 0 10px 0;
}
.text p:last-child {
  margin-bottom: 0;
}
</style>
